import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { gameInstance } from '@Game/GameComponent';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IAuthenticateGameConfig } from '@GFTypes/GameStoreTypes/AuthenticateGameConfig';
import { IAuthenticateResponseData } from '@GFTypes/Responses/AuthenticateResponse';
import { processAccountData } from '../Data/AccountDataProcessor';
import { processBetData } from '../Data/BetDataProcessor';
import { processBonusBuyData } from '../Data/BonusBuyDataProcessor';
import { processCollectorData } from '../Data/CollectorDataProcessor';
import { processErrorData } from '../Data/ErrorDataProcessor';
import { processFreeGamesSpinData } from '../Data/FreeGamesDataProcessor';
import { processLockAndRollAuthData, processLockAndRollSpinData } from '../Data/LockAndRollDataProcessor';
import { processPayTableSymbols } from '../Data/PayTableSymbolsDataProcessor';
import { filterEmptyReels, normalizeStartOrFinalSymbols, normalizeWinningLine } from '../Utils/SymbolIndexNormalizerUtils';
import { processSpinResponse } from './SpinResponseProcessor';
import { isDoubleLockAndRollCoinPresent } from '../Data/DoubleLockAndRollCoinDataProcessor';

export const processAuthenticateResponse = (authenticateData: IAuthenticateResponseData, gameStore: GameStore) => {
    const clone = structuredClone(authenticateData);
    CurrencyUtils.init(clone.outcome.gameConfig.finalDivider, clone.account.currency.exponent, clone.account.currency.code);
    clone.outcome.startSymbols = normalizeStartOrFinalSymbols(clone.outcome.startSymbols);
    if (clone.error && clone.code) {
        gameStore.set(EGameStoreKeys.GeneralAppState, { error: processErrorData(clone.error, clone.code) });
    } else {
        gameStore.set(EGameStoreKeys.Account, processAccountData(authenticateData,gameStore));
        const bonusBuyInitData = processBonusBuyData(authenticateData);
        if (bonusBuyInitData) {
            gameStore.set(
                EGameStoreKeys.BonusBuy,
                {
                    bonusBuyInitData
                },
                true
            );
        }
        gameStore.set(
            EGameStoreKeys.BetState,
            processBetData({
                defaults: authenticateData.bets,
                account: clone.account,
                currentState: gameStore.get(EGameStoreKeys.BetState),
                bonuses: gameStore.get(EGameStoreKeys.Account).bonuses,
                lastBaseGameBet: authenticateData.outcome?.currentBet
            })
        );
        gameStore.set(EGameStoreKeys.AuthenticateGameConfig, mapAuthenticateResponseToGameConfig(clone));
        gameStore.set(EGameStoreKeys.AuthenticateStartSymbols, clone.outcome.startSymbols);
        gameStore.set(EGameStoreKeys.StartHistorySymbols, clone.outcome.startSymbols);
        gameStore.set(EGameStoreKeys.GeneralAppState, {
            insufficientFunds: !gameStore.get(EGameStoreKeys.BetState).available && gameInstance.betManager.getCurrentSpinBet() > 0
        });
        gameStore.set(EGameStoreKeys.NumberOfReels, clone.outcome.startSymbols.length);
        gameStore.set(EGameStoreKeys.NumberOfSymbolsPerReel, clone.outcome.startSymbols[0].length - 2);
        gameStore.set(EGameStoreKeys.LastSpinBet, clone.outcome.currentBet);
        const lockAndRollConfig = processLockAndRollAuthData(clone.outcome);
        if (lockAndRollConfig) gameStore.set(EGameStoreKeys.LockAndRoll, lockAndRollConfig, true);

        if (clone.resume && clone.outcome.resume) {
            gameStore.set(EGameStoreKeys.NextActions, clone.resume.nextActions);

            const processedOutcome = processSpinResponse({ ...clone, result: { ...clone.result, outcome: clone.outcome.resume } });
            if (processedOutcome) gameStore.set(EGameStoreKeys.ResumedSpinOutcome, processedOutcome);
            const processedFreeGames = processFreeGamesSpinData(processedOutcome);
            if (processedFreeGames) gameStore.set(EGameStoreKeys.FreeGames, processedFreeGames, true);
            const processedLockAndRoll = processLockAndRollSpinData(processedOutcome);
            if (processedLockAndRoll) gameStore.set(EGameStoreKeys.LockAndRoll, processedLockAndRoll, true);
        }
    }
};

const mapAuthenticateResponseToGameConfig = (authenticateData: IAuthenticateResponseData): IAuthenticateGameConfig => {
    const rawGameConfig = authenticateData.outcome.gameConfig;
    const processedSymbols = processPayTableSymbols(rawGameConfig);
    const gameConfig: IAuthenticateGameConfig = {
        ...rawGameConfig,
        symbols: processedSymbols,
        payLines: rawGameConfig.payLines.map((payLine) => normalizeWinningLine(payLine)),
        gamble: {
            ...rawGameConfig.gamble,
            halfMinValue: CurrencyUtils.createCurrencyValue(rawGameConfig.gamble.halfMinValue),
            maxWin: CurrencyUtils.createCurrencyValue(rawGameConfig.gamble.maxWin),
            minWin: CurrencyUtils.createCurrencyValue(rawGameConfig.gamble.minWin)
        },
        defaultCollectors: rawGameConfig.defaultCollectors ?? undefined,
        collectors: rawGameConfig.collectors ? processCollectorData(rawGameConfig.collectors, rawGameConfig.defaultCollectors) : undefined,
        bonusBuy: rawGameConfig.bonusBuy,
        spinningReels: filterEmptyReels(rawGameConfig.spinningReels)
    };
    const doubleReels = isDoubleLockAndRollCoinPresent(gameConfig);
    if (doubleReels) gameConfig.doubleSpinningReels = rawGameConfig.spinningReels;
    return gameConfig;
};
